import React, { useEffect, useState,useRef } from 'react';
import { ColorInput, ToggleCheckbox } from '../../utils/gridUtils';
import {
  FOLIAGE_TREE_COLOR_DEFAULTS,
  FOLIAGE_GRASS_COLOR_DEFAULT,
  FOLIAGE_BUSH_COLOR_DEFAULT,
} from '../../utils/particles/handleFoliage';
import { previewCloudColor } from '../../utils/particles/handleClouds';
import { getGridHistoryScene } from '../../utils/gridHistory';
import { useSelector, useDispatch } from 'react-redux';
import {
  playParticle,
  pauseParticle,
  stopParticle,
} from '../../redux/Slice/particleSlice';

// Import the Grid components
import { GridDropdown, GridTextInput } from '../../utils/gridUtils';
import { iconsPath } from '../../utils/imagePath';
import {
  PARTICLE_SHAPE_OPTIONS,
  getDefaultParticleShape,
} from '../../utils/particles/particleShapeTextures';


const ParticleComponent = ({ particle = {}, onChange }) => {
  // Initialize with terrain defaults if type is terrain
  const getInitialData = () => {
    const data = { ...particle };
    if (!data.shape && data.type) {
      data.shape = getDefaultParticleShape(data.type);
    }
    if (data.type === 'foliage') {
      data.pineColor = data.pineColor || FOLIAGE_TREE_COLOR_DEFAULTS.pine;
      data.oakColor = data.oakColor || FOLIAGE_TREE_COLOR_DEFAULTS.oak;
      data.birchColor = data.birchColor || FOLIAGE_TREE_COLOR_DEFAULTS.birch;
      data.grassColor =
        data.grassColor || data.color || FOLIAGE_GRASS_COLOR_DEFAULT;
      data.bushColor = data.bushColor || data.color || FOLIAGE_BUSH_COLOR_DEFAULT;
      data.color = data.grassColor;
    }
    if (data.type === 'terrain') {
      // Force intensity to 'Medium' for terrain (convert from number if needed)
      if (
        !data.intensity ||
        data.intensity === '' ||
        typeof data.intensity === 'number' ||
        (data.intensity !== 'Low' &&
          data.intensity !== 'Medium' &&
          data.intensity !== 'High')
      ) {
        data.intensity = 'Medium';
      }
      // Set blending default to 'NormalBlending' for terrain only if missing or invalid
      const validBlendings = [
        'NormalBlending',
        'AdditiveBlending',
        'MultiplyBlending',
      ];
      if (
        !data.blending ||
        data.blending === '' ||
        !validBlendings.includes(data.blending)
      ) {
        data.blending = 'NormalBlending';
      }
    }
    return data;
  };

  const initialData = getInitialData();
  const [addData, setAddData] = useState(initialData);
  const [localData, setLocalData] = useState(initialData);
  const [showWarnings, setShowWarnings] = useState({
    particleCount: false,
    size: false,
    opacity: false,
    treeCount: false,
    bushCount: false,
    grassCount: false,
    range: false,
    waveSpeed: false,
    life: false,
    lifeVariation: false,
  });
  const [expandedSections, setExpandedSections] = useState({
    animation: true,
    basic: true,
    material: false,
    shape: false,
    physics: false,
    lifecycle: false,
    color: false,
    size: false,
    typeSpecific: false,
  });
  const dispatch = useDispatch();
  const lastTyped = useRef(0);


  // Sanitize data before emitting to parent (prevents invalid values
  // such as empty string / NaN for terrain dimensions that can crash
  // canvas operations downstream).
  const sanitizeForEmit = (data) => {
    const copy = { ...data };
    if (copy.type === 'terrain') {
      // Ensure terrainHeight and terrainWidth are positive integers >= 1
      const h = Number(copy.terrainHeight);
      const w = Number(copy.terrainWidth);
      copy.terrainHeight = Number.isFinite(h) && h >= 1 ? Math.floor(h) : 1;
      copy.terrainWidth = Number.isFinite(w) && w >= 1 ? Math.floor(w) : 1;
    }
    return copy;
  };

  // Emit immediately so every property edit is captured by undo/redo history.
  const emitChange = (updatedData) => {
    if (onChange) {
      onChange(sanitizeForEmit(updatedData));
    }
  };

  useEffect(() => {
    if (!particle) return;
    if (Date.now() - lastTyped.current < 600) return;
    // Apply terrain defaults if needed
    let updatedParticle = { ...particle };
    let defaultsApplied = false;

    if (!updatedParticle.shape && updatedParticle.type) {
      updatedParticle.shape = getDefaultParticleShape(updatedParticle.type);
      defaultsApplied = true;
    }

    if (particle.type === 'terrain') {
      // Force intensity to 'Medium' for terrain (convert from number if needed)
      const currentIntensity = particle.intensity;
      const validIntensities = ['Low', 'Medium', 'High'];
      if (
        !currentIntensity ||
        currentIntensity === '' ||
        typeof currentIntensity === 'number' ||
        !validIntensities.includes(currentIntensity)
      ) {
        updatedParticle.intensity = 'Medium';
        if (currentIntensity !== 'Medium') {
          defaultsApplied = true;
        }
      }

      // Set blending default to 'NormalBlending' for terrain only if missing or invalid
      const currentBlending = particle.blending;
      const validBlendings = [
        'NormalBlending',
        'AdditiveBlending',
        'MultiplyBlending',
      ];
      if (
        !currentBlending ||
        currentBlending === '' ||
        !validBlendings.includes(currentBlending)
      ) {
        updatedParticle.blending = 'NormalBlending';
        if (currentBlending !== 'NormalBlending') {
          defaultsApplied = true;
        }
      }

      // Always update for terrain particles to ensure defaults are shown
      setAddData(updatedParticle);
      setLocalData(updatedParticle);
      // If defaults were applied, trigger onChange
      if (defaultsApplied && onChange) {
        onChange(updatedParticle);
      }
    } else {
      // For non-terrain particles, sync normally
      const isEqual = (a, b) => {
        const aKeys = Object.keys(a || {});
        const bKeys = Object.keys(b || {});
        if (aKeys.length !== bKeys.length) return false;
        for (let key of aKeys) {
          if (a[key] !== b[key]) return false;
        }
        return true;
      };

      if (!isEqual(addData, updatedParticle)) {
        setAddData(updatedParticle);
        setLocalData(updatedParticle);
      }
      if (defaultsApplied && onChange) {
        onChange(updatedParticle);
      }
    }
  }, [particle]);

  // useEffect(() => {
  //   // Only reset local state when switching to a different particle
  //   if (particle.id !== localData.id) {
  //     setAddData({ ...particle });
  //     setLocalData({ ...particle });
  //   }
  // }, [particle.id]); // Only depend on ID changes

  const handleNumberChange = (e, field) => {
    const value = e.target.value;

    // Allow empty value or numbers with at most one decimal point
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      // Convert to number before passing to handleChange
      const numValue = value === '' ? '' : Number(value);
      handleChange(numValue, field);
    }
  };

  const handleChange = (value, field) => {
    lastTyped.current = Date.now();
    const updated = { ...localData, [field]: value };
    setLocalData(updated);
    setAddData(updated);
    emitChange(updated);
  };

  /** Live color while dragging — paint meshes now, commit Redux on release. */
  const handleLiveColorChange = (hex) => {
    const particleId = localData.id || particle.id || particle._id;
    if (!particleId) return;

    const scene = getGridHistoryScene();
    const obj = scene?.getObjectByName?.(particleId);
    if (obj?.objType === 'clouds') {
      previewCloudColor(obj, hex);
    }
  };

  const handleNumberKeyPress = (e) => {
    const char = String.fromCharCode(e.which);
    if (!/[0-9.]/.test(char)) {
      e.preventDefault();
    }
  };

  const handleVectorChange = (value, field, axis) => {
    const currentVector = localData[field] || { x: 0, y: 0, z: 0 };
    const updatedVector = { ...currentVector, [axis]: parseFloat(value) || 0 };
    handleChange(updatedVector, field);
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };
  const MAX_SAFE_PARTICLES =
    particle.type === 'terrain'
      ? 10005
      : particle.type === 'clouds'
        ? 100
        : 100000;

  // Animation control handlers
  const handlePlay = () => {
    if (localData.id) {
      dispatch(playParticle(localData.id));
      const updated = { ...localData, isPlaying: true, isStopped: false };
      setLocalData(updated);
      setAddData(updated);
      emitChange(updated);
    }
  };

  const handlePause = () => {
    if (localData.id) {
      dispatch(pauseParticle(localData.id));
      const updated = { ...localData, isPlaying: false, isStopped: false };
      setLocalData(updated);
      setAddData(updated);
      emitChange(updated);
    }
  };

  const handleStop = () => {
    if (localData.id) {
      dispatch(stopParticle(localData.id));
      const updated = { ...localData, isPlaying: false, isStopped: true };
      setLocalData(updated);
      setAddData(updated);
      emitChange(updated);
    }
  };
  const handleCountChange = (value, key, max) => {
    if (value === '') {
      handleChange('', key);
      return;
    }
    const inputValue = parseInt(value) || 0;
    const capped = Math.max(0, Math.min(inputValue, max));

    handleChange(capped, key);

    if (inputValue > max) {
      setShowWarnings((prev) => ({
        ...prev,
        [key]: true,
      }));

      setTimeout(() => {
        setShowWarnings((prev) => ({
          ...prev,
          [key]: false,
        }));
      }, 2000);
    }
  };



  const renderVectorInput = (label, field, placeholder = '0') => (
    <div className="flex items-center gap-7 mb-2">
      {' '}
      {/* Changed mb-3 to mb-2 */}
      <label className="text-[10px] font-medium text-[#A6A6A6] min-w-[60px]">
        {label}
      </label>
      <div className="flex gap-2">
        {['x', 'y', 'z'].map((axis) => (
          <div key={axis} className="flex items-center gap-1">
            <span className="text-[9px] text-gray-400 uppercase">{axis}</span>
            <input
              type="number"
              value={localData[field]?.[axis] || ''}
              onChange={(e) => handleVectorChange(e.target.value, field, axis)}
              onKeyPress={handleNumberKeyPress}
              className="w-7 px-1 py-0.5 text-[10px] bg-[#2b2c2f] rounded text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none transition-colors"
            />
          </div>
        ))}
      </div>
    </div>
  );

  const renderNumberInput = (
    label,
    field,
    min = 0,
    max = 1,
    step = 0.1,
    placeholder = '0'
  ) => (
    <div className="mb-3 ">
      <GridTextInput
        label={label}
        value={localData[field] ?? ''}
        onChange={(e) => handleNumberChange(e, field)}
        placeholder={placeholder}
        type="number"
        min={min}
        max={max}
        step={step}
      />
    </div>
  );

  const renderCheckbox = (label, field) => (
    <div className="flex items-center mb-3">
      <input
        type="checkbox"
        checked={localData[field] || false}
        onChange={(e) => handleChange(!localData[field], field)}
        className="w-3 h-3 mr-2"
      />
      <label className="text-[10px] text-white">{label}</label>
    </div>
  );

  const renderSection = (title, sectionKey, children) => (
    <div className="border-b border-[#2D2E32] last:border-b-0  ">
      <button
        onClick={() => toggleSection(sectionKey)}
        className="w-full flex items-center justify-between py-2 text-left focus:outline-none"
      >
        <h3 className="text-[11px] font-semibold text-white">{title}</h3>
        <span className="text-white text-[14px] font-mono w-4 text-center">
          {expandedSections[sectionKey] ? '−' : '+'}
        </span>
      </button>
      {expandedSections[sectionKey] && <div className="pb-2">{children}</div>}
    </div>
  );

  const renderDropdown = (label, field, options, menuClassName) => (
    <div className="mb-3">
      <GridDropdown
        label={label}
        value={localData[field] || ''}
        onChange={(e) => handleChange(e.target.value, field)}
        options={options}
        menuClassName={menuClassName}
      />
    </div>
  );

  const renderTextInput = (label, field, type = 'text', placeholder = '') => (
    <div className="mb-3 flex items-center gap-[61px]">
      <label className="block text-[10px] font-medium text-[#A6A6A6] mb-1">
        {label}
      </label>
      <input
        // type={type}
        // placeholder={placeholder}
        value={
          localData[field] !== undefined && localData[field] !== null && localData[field] !== ''
            ? label === 'Segments'
              ? Math.min(localData[field], 500)
              : localData[field]
            : label === 'Range'
              ? '0.1'
              : ''
        }
        onChange={(e) =>
          type === 'number'
            ? handleNumberChange(e, field)
            : handleChange(e.target.value, field)
        }
        className="w-full px-2 py-1 text-[10px] bg-[#2b2c2f] rounded text-white placeholder-gray-400  focus:outline-none transition-colors"
        placeholder={placeholder}
        type={type}
      />
    </div>
  );

  const renderTwoColumnInputs = (leftInput, rightInput) => (
    <div className="grid grid-cols-1 gap-2 ">
      <div>{leftInput}</div>
      <div>{rightInput}</div>
    </div>
  );

  return (
    <div className="w-full  overflow-y-auto scrollbar text-white">
      {/* Animation Controls */}
      {localData.type !== 'terrain' &&
        renderSection(
          'Animation Controls',
          'animation',
          <div className="space-y-2">
            <div className="flex items-center justify-between mb-2">
              {/* <span className="text-[10px] text-gray-300">Animation State</span> */}
              <div className="flex items-center space-x-1">
                {/* <span
                className={`w-2 h-2 rounded-full ${localData.isPlaying ? 'bg-green-400' : localData.isStopped ? 'bg-red-400' : 'bg-yellow-400'}`}
              ></span> */}
                {/* <span className="text-[8px] text-gray-400">
                {localData.isPlaying
                  ? 'Playing'
                  : localData.isStopped
                    ? 'Stopped'
                    : 'Paused'}
              </span> */}
              </div>
            </div>

            <div className="flex space-x-2">
              <button
                onClick={handlePlay}
                disabled={localData.isPlaying && !localData.isStopped}
                className={`flex-1 px-2 py-1.5 text-[10px] rounded transition-colors flex items-center justify-center gap-2 ${
                  localData.isPlaying && !localData.isStopped
                    ? 'bg-[#2b2c2f] text-gray-400 cursor-not-allowed'
                    : 'bg-[#2b2c2f] text-white'
                }`}
              >
                <img className="h-3 w-3" src={iconsPath.playParticale} alt="" />
                Play
              </button>
              <button
                onClick={handleStop}
                disabled={localData.isStopped}
                className={`flex-1 px-2 py-1.5 text-[8px] rounded transition-colors flex items-center justify-center gap-2  ${
                  localData.isStopped
                    ? 'bg-[#2b2c2f] text-gray-400 cursor-not-allowed'
                    : 'bg-[#2b2c2f] text-white'
                }`}
              >
                <img
                  className="h-3 w-3"
                  src={iconsPath?.pausePartical}
                  alt=""
                />{' '}
                Pause
              </button>
            </div>
          </div>
        )}
      {renderSection(
        'Basic Properties',
        'basic',
        <>
          {localData.type !== 'water' &&
            localData.type !== 'terrain' &&
            localData.type !== 'stars' &&
            localData.type !== 'clouds' &&
            localData.type !== 'fire' &&
            localData.type !== 'rain' &&
            localData.type !== 'snow' &&
            localData.type !== 'foliage' &&
            renderDropdown('Intensity', 'intensity', [
              { value: 'Low', label: 'Low' },
              { value: 'Medium', label: 'Medium' },
              { value: 'High', label: 'High' },
            ])}
          <GridTextInput
            label="Range"
            value={localData.range ?? ''}
            type="number"
            minValue={0} // ✅ This will now prevent negative input
            maxValue={100}
            placeholder="Enter range"
            onMaxReached={() => {
              setShowWarnings((prev) => ({ ...prev, range: true }));
              setTimeout(() => {
                setShowWarnings((prev) => ({ ...prev, range: false }));
              }, 2000);
            }}
            onChange={(e) => {
              const value = e.target.value === '' ? '' : Number(e.target.value);
              handleChange(value, 'range');
            }}
          />

          {showWarnings.range && (
            <p className="text-[#EE2B2A] text-[10px] mt-[-8px] mb-2">
              Maximum range: 100
            </p>
          )}

          {localData.type !== 'foliage' && (
            <div className="mb-3">
              <ColorInput
                label="Color"
                color={localData.color}
                commitOnComplete={localData.type === 'clouds'}
                onLiveChange={
                  localData.type === 'clouds'
                    ? handleLiveColorChange
                    : undefined
                }
                setColor={(value) => handleChange(value, 'color')}
              />
            </div>
          )}
          {localData.type !== 'terrain' &&
            localData.type !== 'water' &&
            localData.type !== 'fire' &&
            localData.type !== 'foliage' && (
              // <GridTextInput
              //   label="Count"
              //   value={localData.particleCount || ''}
              //   onChange={(e) => {
              //     const inputValue = parseInt(e.target.value) || 0;
              //     handleChange(inputValue, 'particleCount');

              //     // Show warning if exceeds max
              //     if (
              //       inputValue >
              //       (particle.type === 'terrain' ? 10005 : MAX_SAFE_PARTICLES)
              //     ) {
              //       setShowWarnings((prev) => ({
              //         ...prev,
              //         particleCount: true,
              //       }));
              //       setTimeout(() => {
              //         setShowWarnings((prev) => ({
              //           ...prev,
              //           particleCount: false,
              //         }));
              //       }, 2000);
              //     }
              //   }}
              //   placeholder="1000"
              //   type="number"
              //   maxDigits={10}
              // />

              // <GridTextInput
              //   label="Count"
              //   value={localData.particleCount || ''}
              //   onChange={(e) => {
              //     const inputValue = parseInt(e.target.value) || 0;

              //     // --- ADD THIS LINE ---
              //     const capped = Math.min(inputValue, 100000);

              //     handleChange(capped, 'particleCount');

              //     // Show warning if exceeds max
              //     if (
              //       capped >
              //       (particle.type === 'terrain' ? 10005 : MAX_SAFE_PARTICLES)
              //     ) {
              //       setShowWarnings((prev) => ({
              //         ...prev,
              //         particleCount: true,
              //       }));
              //       setTimeout(() => {
              //         setShowWarnings((prev) => ({
              //           ...prev,
              //           particleCount: false,
              //         }));
              //       }, 2000);
              //     }
              //   }}
              //   placeholder="1000"
              //   type="number"
              //   maxDigits={6}
              //   max={100000}
              // />

              <GridTextInput
                label="Count"
                value={localData.particleCount ?? ''}
                type="number"
                placeholder="Enter Count"
                // ✅ Dynamic limits
                minValue={1}
                maxValue={
                  particle.type === 'clouds'
                    ? 100
                    : particle.type === 'terrain'
                      ? 10005
                      : MAX_SAFE_PARTICLES
                }
                maxDigits={particle.type === 'clouds' ? 3 : 6}
                // ✅ Normal change
                onChange={(e) => {
                  const inputValue = e.target.value === '' ? '' : parseInt(e.target.value);
                  handleChange(inputValue, 'particleCount');
                }}
                // ✅ Max limit warning
                onMaxReached={(max) => {
                  setShowWarnings((prev) => ({
                    ...prev,
                    particleCount: true,
                  }));

                  setTimeout(() => {
                    setShowWarnings((prev) => ({
                      ...prev,
                      particleCount: false,
                    }));
                  }, 2000);
                }}

                // ✅ Min limit warning (optional)
                // onMinReached={(min) => {
                //   setShowWarnings((prev) => ({
                //     ...prev,
                //     particleCount: true,
                //   }));

                //   setTimeout(() => {
                //     setShowWarnings((prev) => ({
                //       ...prev,
                //       particleCount: false,
                //     }));
                //   }, 2000);
                // }}
              />
            )}
          {showWarnings.particleCount && (
            <p className="text-[#EE2B2A] text-[10px] mt-[-8px] mb-2">
              {localData.particleCount < 1
                ? 'Minimum count: 1'
                : particle.type === 'clouds'
                  ? 'Maximum count: 100'
                  : particle.type === 'terrain'
                    ? 'Maximum count: 10005'
                    : 'Maximum count: 100000'}
            </p>
          )}
        </>
      )}

      {/* Material Properties */}
      {renderSection(
        'Material',
        'material',
        <>
          {renderTwoColumnInputs(
            <div className="flex items-center gap-[69px]">
              {localData.type !== 'water' &&
                // localData.type !== 'rain' &&
                localData.type !== 'terrain' && (
                  <label className=" text-[10px] font-medium text-[#A6A6A6] ">
                    Size
                  </label>
                )}
              {localData.type !== 'water' &&
                // localData.type !== 'rain' &&
                localData.type !== 'terrain' && (
                  <div className="flex-1">
                    <input
                      type="number"
                      min={0.1}
                      max={5}
                      step={0.1}
                      placeholder="Enter Size"
                      value={localData.size || ''}
                      onChange={(e) => {
                        const inputValue = e.target.value;

                        // Allow empty string for clearing
                        if (inputValue === '') {
                          handleChange('', 'size');
                          setShowWarnings((prev) => ({ ...prev, size: false }));
                          return;
                        }

                        const numValue = parseFloat(inputValue);

                        // Only update if it's a valid number
                        if (!isNaN(numValue)) {
                          // Clamp between 0.1 and 5
                          const clampedValue = Math.max(
                            0.1,
                            Math.min(5, numValue)
                          );
                          handleChange(clampedValue, 'size');

                          // Show warning if user tried to enter more than 5
                          if (numValue > 5) {
                            setShowWarnings((prev) => ({
                              ...prev,
                              size: true,
                            }));
                            setTimeout(() => {
                              setShowWarnings((prev) => ({
                                ...prev,
                                size: false,
                              }));
                            }, 2000);
                          }
                        }
                      }}
                      onKeyPress={handleNumberKeyPress}
                      className="w-full px-2 py-1 text-[10px] bg-[#2b2c2f] rounded text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none transition-colors"
                    />
                    {showWarnings.size && (
                      <p className="text-[#EE2B2A] text-[10px] mt-[2px]">
                        Maximum size: 5
                      </p>
                    )}
                  </div>
                )}
            </div>,
            <div className="flex flex-col mb-3">
              <div className="flex items-center gap-[52px]">
                {localData.type !== 'stars' &&
                  // localData.type !== 'clouds' &&
                  localData.type !== 'fire' && (
                    <label className=" text-[10px] font-medium text-[#A6A6A6] ">
                      Opacity
                    </label>
                  )}
                {localData.type !== 'stars' &&
                  // localData.type !== 'clouds' &&
                  localData.type !== 'fire' && (
                    <input
                      type="number"
                      min={0}
                      max={1}
                      step={0.1}
                      placeholder="Enter Opacity"
                      value={
                        localData.opacity !== undefined &&
                        localData.opacity !== null
                          ? localData.opacity
                          : ''
                      }
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === '' || raw === '.') {
                          handleChange(raw, 'opacity');
                          return;
                        }
                        const inputValue = parseFloat(raw);
                        const numeric = isNaN(inputValue) ? 0 : inputValue;
                        const value = Math.min(1, numeric);
                        if (numeric > 1) {
                          setShowWarnings((prev) => ({
                            ...prev,
                            opacity: true,
                          }));
                          setTimeout(() => {
                            setShowWarnings((prev) => ({
                              ...prev,
                              opacity: false,
                            }));
                          }, 2000);
                        }
                        handleChange(value, 'opacity');
                      }}
                      onKeyPress={handleNumberKeyPress}
                      className="w-full px-2 py-1 text-[10px] bg-[#2b2c2f] rounded text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none transition-colors"
                    />
                  )}
              </div>
              {showWarnings.opacity && (
                <p className="text-[#EE2B2A] text-[10px] mt-1">
                  Maximum opacity: 1
                </p>
              )}
            </div>
          )}
          {localData.type !== 'terrain' &&
            localData.type !== 'water' &&
            localData.type !== 'fire' &&
            localData.type !== 'clouds' &&
            localData.type !== 'rain' &&
            localData.type !== 'snow' &&
            localData.type !== 'stars' &&
            localData.type !== 'foliage' && (
              <ToggleCheckbox
                label="Size Attenuation"
                checked={localData.sizeAttenuation || false}
                onToggle={(e) =>
                  handleChange(!localData.sizeAttenuation, 'sizeAttenuation')
                }
              />
            )}
          {localData.type !== 'stars' &&
            localData.type !== 'clouds' &&
            localData.type !== 'water' &&
            localData.type !== 'rain' &&
            localData.type !== 'snow' &&
            localData.type !== 'fire' &&
            localData.type !== 'foliage' &&
            renderDropdown('Blending', 'blending', [
              { value: 'NormalBlending', label: 'Normal' },
              { value: 'AdditiveBlending', label: 'Additive' },
              { value: 'MultiplyBlending', label: 'Multiply' },
            ])}
        </>
      )}

      {/* Shape Properties */}
      {localData.type !== 'terrain' &&
        localData.type !== 'water' &&
        localData.type !== 'clouds' &&
        localData.type !== 'foliage' &&
        renderSection(
          'Shape',
          'shape',
          <>
            {renderDropdown(
              'Shape',
              'shape',
              PARTICLE_SHAPE_OPTIONS,
              'custom-scrollbar'
            )}
            {localData.type !== 'rain' &&
              localData.type !== 'snow' &&
              localData.type !== 'fire' &&
              localData.type !== 'stars' && (
                <GridTextInput
                  label="Scale"
                  value={localData.shapeScale ?? ''}
                  onChange={(e) => handleNumberChange(e, 'shapeScale')}
                  placeholder="1.0"
                  type="number"
                  min={0.1}
                  max={3}
                  step={0.1}
                />
              )}
            {localData.type !== 'rain' &&
              localData.type !== 'snow' &&
              localData.type !== 'fire' &&
              localData.type !== 'stars' && (
                <GridTextInput
                  label="Rotation"
                  value={localData.shapeRotation ?? ''}
                  onChange={(e) => handleNumberChange(e, 'shapeRotation')}
                  placeholder="0.0"
                  type="number"
                  min={0}
                  max={Math.PI * 2}
                  step={0.1}
                />
              )}
          </>
        )}

      {/* Physics Properties */}
      {localData.type !== 'terrain' &&
        localData.type !== 'water' &&
        localData.type !== 'clouds' &&
        localData.type !== 'rain' &&
        localData.type !== 'snow' &&
        localData.type !== 'foliage' &&
        localData.type !== 'fire' &&
        localData.type !== 'stars' &&
        renderSection(
          'Physics',
          'physics',
          <>
            {renderVectorInput('Velocity', 'velocity')}
            {renderVectorInput('Acceleration', 'acceleration')}
            {renderNumberInput('Turbulence', 'turbulence', 0, 1, 0.01, '0.0')}
            {renderVectorInput('Wind', 'wind')}
          </>
        )}

      {/* Life Cycle */}
      {renderSection(
        'Life Cycle',
        'lifecycle',
        <>
          <GridTextInput
            label="Life(sec)"
            value={localData.life ?? ''}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === '' || raw === '.') {
                handleChange(raw, 'life');
                return;
              }
              handleNumberChange(e, 'life');
            }}
            placeholder="Enter Life Cycle"
            type="number"
            minValue={0.1}
            maxValue={120}
            step={0.1}
            onMaxReached={() => {
              setShowWarnings((prev) => ({ ...prev, life: true }));
              setTimeout(() => {
                setShowWarnings((prev) => ({ ...prev, life: false }));
              }, 2000);
            }}
          />
          {showWarnings.life && (
            <p className="text-[#EE2B2A] text-[10px] mt-[-8px] mb-2">
              Maximum life: 120
            </p>
          )}
          {localData.type !== 'stars' &&
            localData.type !== 'fire' &&
            localData.type !== 'clouds' &&
            localData.type !== 'rain' &&
            localData.type !== 'snow' && 
            localData.type !== 'foliage' &&
            localData.type !== 'water' &&
            localData.type !== 'terrain' && (
              <GridTextInput
                label="Variation"
                value={localData.lifeVariation ?? ''}
                onChange={(e) => handleNumberChange(e, 'lifeVariation')}
                placeholder="0.0"
                type="number"
                minValue={0}
                maxValue={1}
                step={0.1}
                onMaxReached={() => {
                  setShowWarnings((prev) => ({ ...prev, lifeVariation: true }));
                  setTimeout(() => {
                    setShowWarnings((prev) => ({
                      ...prev,
                      lifeVariation: false,
                    }));
                  }, 3000);
                }}
              />
            )}
          {showWarnings.lifeVariation && (
            <p className="text-[#EE2B2A] text-[10px] mt-[-8px] mb-2">
              Maximum variation: 1
            </p>
          )}
        </>
      )}

      {/* Color Properties */}
      {localData.type !== 'terrain' &&
        localData.type !== 'water' &&
        localData.type !== 'clouds' &&
        localData.type !== 'rain' &&
        localData.type !== 'snow' &&
        localData.type !== 'fire' &&
        localData.type !== 'foliage' &&
        localData.type !== 'stars' &&
        renderSection(
          'Color',
          'color',
          <>
            {renderNumberInput('Variation', 'colorVariation', 0, 1, 0.1, '0.0')}
            <ToggleCheckbox
              label="Color Transition"
              checked={localData.colorTransition || false}
              onToggle={(e) =>
                handleChange(!localData.colorTransition, 'colorTransition')
              }
            />
          </>
        )}

      {/* Size Properties */}
      {localData.type !== 'terrain' &&
        localData.type !== 'water' &&
        localData.type !== 'clouds' &&
        localData.type !== 'rain' &&
        localData.type !== 'snow' &&
        localData.type !== 'fire' &&
        localData.type !== 'foliage' &&
        localData.type !== 'stars' &&
        renderSection(
          'Size',
          'size',
          <>
            {renderNumberInput('Variation', 'sizeVariation', 0, 1, 0.1, '0.0')}
            <ToggleCheckbox
              label="Size Transition"
              checked={localData.sizeTransition || false}
              onToggle={(e) =>
                handleChange(!localData.sizeTransition, 'sizeTransition')
              }
            />
          </>
        )}

      {/* Type-specific Properties */}
      {(localData.type === 'fire' ||
        // localData.type === 'rain' ||
        // localData.type === 'snow' ||
        localData.type === 'stars' ||
        // localData.type === 'clouds' ||
        localData.type === 'terrain' ||
        localData.type === 'water' ||
        localData.type === 'foliage') &&
        renderSection(
          `${localData.type?.charAt(0).toUpperCase() + localData.type?.slice(1)} Properties`,
          'typeSpecific',
          <>
            {localData.type === 'fire' && (
              <>
                <GridTextInput
                  label="Core Count"
                  value={localData.coreCount ?? ''}
                  minValue={0}
                  onChange={(e) =>
                    handleCountChange(e.target.value, 'coreCount', 3000)
                  }
                  placeholder="300"
                  type="number"
                  maxDigits={4}
                  maxValue={3000}
                  onMaxReached={(max) => {
                    setShowWarnings((prev) => ({ ...prev, coreCount: true }));
                    setTimeout(() => {
                      setShowWarnings((prev) => ({
                        ...prev,
                        coreCount: false,
                      }));
                    }, 2000);
                    handleChange(max, 'coreCount');
                  }}
                />
                {showWarnings.coreCount && (
                  <p className="text-[#EE2B2A] text-[10px] mt-[-2px] mb-2">
                    Maximum core count: 3000
                  </p>
                )}

                {localData.type !== 'fire' && (
                  <GridTextInput
                    label="Ember Count"
                    value={localData.emberCount ?? ''}
                    onChange={(e) => handleNumberChange(e, 'emberCount')}
                    placeholder="150"
                    type="number"
                  />
                )}

                {localData.type !== 'fire' && (
                  <GridTextInput
                    label="Heat Intensity"
                    value={localData.heatIntensity ?? ''}
                    onChange={(e) => handleNumberChange(e, 'heatIntensity')}
                    placeholder="0.5"
                    type="number"
                    min={0}
                    max={1}
                    step={0.1}
                  />
                )}
              </>
            )}

            {localData.type === 'rain' && (
              <>
                {renderNumberInput(
                  'Drop Size',
                  'dropSize',
                  0.01,
                  0.5,
                  0.01,
                  '0.05'
                )}
                <ToggleCheckbox
                  label="Splash Effect"
                  checked={localData.splashEffect || false}
                  onToggle={(e) =>
                    handleChange(!localData.splashEffect, 'splashEffect')
                  }
                />
              </>
            )}

            {localData.type === 'snow' && (
              <>
                <GridTextInput
                  label="Flake Size"
                  value={localData.flakeSize ?? ''}
                  onChange={(e) => handleNumberChange(e, 'flakeSize')}
                  placeholder="0.05"
                  type="number"
                  min={0.01}
                  max={0.2}
                  step={0.01}
                />
                <GridTextInput
                  label="Wind Effect"
                  value={localData.windEffect ?? ''}
                  onChange={(e) => handleNumberChange(e, 'windEffect')}
                  placeholder="0.0"
                  type="number"
                  min={0}
                  max={1}
                  step={0.1}
                />
              </>
            )}

            {localData.type === 'stars' && (
              <>
                <GridTextInput
                  label="Twinkle Speed"
                  value={localData.twinkleSpeed ?? ''}
                  onChange={(e) => handleNumberChange(e, 'twinkleSpeed')}
                  placeholder="1.0"
                  type="number"
                  minValue={0.1}
                  maxValue={5}
                  step={0.1}
                  onMaxReached={() => {
                    setShowWarnings((prev) => ({
                      ...prev,
                      twinkleSpeed: true,
                    }));
                    setTimeout(() => {
                      setShowWarnings((prev) => ({
                        ...prev,
                        twinkleSpeed: false,
                      }));
                    }, 2000);
                  }}
                  onMinReached={() => {
                    setShowWarnings((prev) => ({
                      ...prev,
                      twinkleSpeed: true,
                    }));
                    setTimeout(() => {
                      setShowWarnings((prev) => ({
                        ...prev,
                        twinkleSpeed: false,
                      }));
                    }, 3000);
                  }}
                />
                {showWarnings.twinkleSpeed && (
                  <p className="text-[#EE2B2A] text-[10px] mt-[-2px] mb-2">
                    Twinkle Speed must be between 0.1 and 5
                  </p>
                )}
                {localData.type !== 'stars' && (
                  <GridTextInput
                    label="Intensity"
                    value={localData.twinkleIntensity ?? ''}
                    onChange={(e) => handleNumberChange(e, 'twinkleIntensity')}
                    placeholder="0.5"
                    type="number"
                    min={0}
                    max={1}
                    step={0.1}
                  />
                )}
              </>
            )}

            {localData.type === 'clouds' && (
              <>
                <GridTextInput
                  label="Cluster Count"
                  value={localData.clusterCount ?? ''}
                  onChange={(e) => handleNumberChange(e, 'clusterCount')}
                  placeholder="15"
                  type="number"
                />
                <GridTextInput
                  label="Cloud Density"
                  value={localData.cloudDensity ?? ''}
                  onChange={(e) => handleNumberChange(e, 'cloudDensity')}
                  placeholder="0.5"
                  type="number"
                  min={0}
                  max={1}
                  step={0.1}
                />
              </>
            )}

            {localData.type === 'terrain' && (
              <>
                <GridTextInput
                  label="Height"
                  value={localData.terrainHeight ?? ''}
                  onChange={(e) => handleNumberChange(e, 'terrainHeight')}
                  placeholder="10"
                  type="number"
                  min={1}
                  max={999}
                  maxDigits={3}
                />
                <GridTextInput
                  label="Width"
                  value={localData.terrainWidth ?? ''}
                  onChange={(e) => handleNumberChange(e, 'terrainWidth')}
                  placeholder="20"
                  type="number"
                  min={1}
                  max={999}
                  maxDigits={3}
                />
                {/*<div className="mb-2 flex items-center gap-2">
                  <label className="block text-[10px] font-medium text-[#A6A6A6] mb-1 min-w-[60px]">
                    Segments
                  </label>
                  <div className="flex-[16] relative flex items-center">
                    <input
                      type="number"
                      value={localData.terrainSegments || ''}
                      onChange={(e) => {
                        const inputValue = parseInt(e.target.value) || 0;
                        const value = Math.min(500, Math.max(0, inputValue));
                        handleChange(value, 'terrainSegments');
                      }}
                      onKeyPress={(e) => {
                        const char = String.fromCharCode(e.which);
                        if (!/[0-9]/.test(char)) {
                          e.preventDefault();
                          return;
                        }
                        // Get current value and check if adding this digit would exceed 500
                        const currentValue = e.target.value;
                        const newValue = parseInt(currentValue + char);
                        if (newValue > 500) {
                          e.preventDefault();
                        }
                      }}
                      onInput={(e) => {
                        // Additional validation on input event
                        const value = parseInt(e.target.value) || 0;
                        if (value > 500) {
                          e.target.value = 500;
                          handleChange(500, 'terrainSegments');
                        }
                      }}
                      placeholder="32"
                      min="0"
                      max="500"
                      className="w-full bg-[#2B2C2F] rounded p-1 text-[10px] text-gray-400 focus:outline-none"
                    />
                  </div>
                </div>*/}
              </>
            )}

            {localData.type === 'water' && (
              <>
                {localData.type !== 'water' && (
                  <GridTextInput
                    label="Wave Height"
                    value={localData.waveHeight ?? ''}
                    onChange={(e) => handleNumberChange(e, 'waveHeight')}
                    placeholder="0.5"
                    type="number"
                    min={0}
                    max={2}
                    step={0.1}
                  />
                )}
                <GridTextInput
                  label="Wave Speed"
                  value={localData.waveSpeed ?? ''}
                  onChange={(e) => handleNumberChange(e, 'waveSpeed')}
                  placeholder="1.0"
                  type="number"
                  minValue={0}
                  maxValue={1}
                  step={0.01}
                  maxDigits={3}
                  onMaxReached={() => {
                    setShowWarnings((prev) => ({ ...prev, waveSpeed: true }));
                    setTimeout(
                      () =>
                        setShowWarnings((prev) => ({
                          ...prev,
                          waveSpeed: false,
                        })),
                      2000
                    );
                  }}
                  onMinReached={() => {
                    setShowWarnings((prev) => ({ ...prev, waveSpeed: true }));
                    setTimeout(
                      () =>
                        setShowWarnings((prev) => ({
                          ...prev,
                          waveSpeed: false,
                        })),
                      3000
                    );
                  }}
                />
                {showWarnings.waveSpeed && (
                  <p className="text-[#EE2B2A] text-[10px] mt-[-2px] mb-2">
                    Wave Speed must be between 0 and 1
                  </p>
                )}
                {localData.type !== 'water' && (
                  <GridTextInput
                    label="Wave Freq"
                    value={localData.waveFrequency ?? ''}
                    onChange={(e) => handleNumberChange(e, 'waveFrequency')}
                    placeholder="1.0"
                    type="number"
                    min={0}
                    max={5}
                    step={0.1}
                  />
                )}
              </>
            )}

            {localData.type === 'foliage' && (
              <>
                <div className="mb-3">
                  <ColorInput
                    label="Pine Tree"
                    color={
                      localData.pineColor || FOLIAGE_TREE_COLOR_DEFAULTS.pine
                    }
                    setColor={(value) => handleChange(value, 'pineColor')}
                  />
                </div>
                <div className="mb-3">
                  <ColorInput
                    label="Oak Tree"
                    color={
                      localData.oakColor || FOLIAGE_TREE_COLOR_DEFAULTS.oak
                    }
                    setColor={(value) => handleChange(value, 'oakColor')}
                  />
                </div>
                <div className="mb-3">
                  <ColorInput
                    label="Birch Tree"
                    color={
                      localData.birchColor || FOLIAGE_TREE_COLOR_DEFAULTS.birch
                    }
                    setColor={(value) => handleChange(value, 'birchColor')}
                  />
                </div>
                <div className="mb-3">
                  <ColorInput
                    label="Grass Color"
                    color={
                      localData.grassColor || FOLIAGE_GRASS_COLOR_DEFAULT
                    }
                    setColor={(value) => handleChange(value, 'grassColor')}
                  />
                </div>
                <div className="mb-3">
                  <ColorInput
                    label="Bush Color"
                    color={localData.bushColor || FOLIAGE_BUSH_COLOR_DEFAULT}
                    setColor={(value) => handleChange(value, 'bushColor')}
                  />
                </div>
                <GridTextInput
                  label="Tree Count"
                  value={localData.treeCount ?? ''}
                  minValue={0}
                  onChange={(e) =>
                    handleCountChange(e.target.value, 'treeCount', 300)
                  }
                  placeholder="10"
                  type="number"
                  maxDigits={3}
                  maxValue={300}
                  onMaxReached={() => {
                    setShowWarnings((prev) => ({ ...prev, treeCount: true }));
                    setTimeout(() => {
                      setShowWarnings((prev) => ({
                        ...prev,
                        treeCount: false,
                      }));
                    }, 2000);
                  }}
                />

                {showWarnings.treeCount && (
                  <p className="text-[#EE2B2A] text-[10px] mt-[-2px] mb-2">
                    Maximum tree count: 300
                  </p>
                )}

                <GridTextInput
                  label="Bush Count"
                  value={localData.bushCount ?? ''}
                  onChange={(e) =>
                    handleCountChange(e.target.value, 'bushCount', 300)
                  }
                  placeholder="10"
                  minValue={0}
                  type="number"
                  maxDigits={3}
                  maxValue={300}
                  onMaxReached={() => {
                    setShowWarnings((prev) => ({ ...prev, bushCount: true }));
                    setTimeout(() => {
                      setShowWarnings((prev) => ({
                        ...prev,
                        bushCount: false,
                      }));
                    }, 2000);
                  }}
                />

                {showWarnings.bushCount && (
                  <p className="text-[#EE2B2A] text-[10px] mt-[-2px] mb-2">
                    Maximum bush count: 300
                  </p>
                )}

                <GridTextInput
                  label="Grass Count"
                  value={localData.grassCount ?? ''}
                  onChange={(e) =>
                    handleCountChange(e.target.value, 'grassCount', 300)
                  }
                  placeholder="50"
                  minValue={0}
                  type="number"
                  maxDigits={3}
                  maxValue={300}
                  onMaxReached={() => {
                    setShowWarnings((prev) => ({ ...prev, grassCount: true }));
                    setTimeout(() => {
                      setShowWarnings((prev) => ({
                        ...prev,
                        grassCount: false,
                      }));
                    }, 2000);
                  }}
                />
                {showWarnings.grassCount && (
                  <p className="text-[#EE2B2A] text-[10px] mt-[-2px] mb-2">
                    Maximum grass count: 300
                  </p>
                )}
              </>
            )}
          </>
        )}
    </div>
  );
};

export default ParticleComponent;
